import cn from 'classnames';
import { useState } from 'react';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { channelAnchor } from '../comments-constants';
import { CommentThread } from './CommentThread';
import { AuditReport } from '../digital-audit-constants';
import { CheckStatus } from '../engine/channel-health-constants';
import { ChannelEvaluation, evaluateChannelHealth } from '../engine/channel-health-utils';

interface ChannelHealthViewProps {
  report: AuditReport | null;
}

const CHECK_MARK: Record<CheckStatus, string> = {
  good: '✓',
  warn: '△',
  miss: '✗',
  info: '•'
};

interface ChannelPanelProps {
  evaluation: ChannelEvaluation;
}

const ChannelPanel = ({ evaluation }: ChannelPanelProps) => {
  const { channel, results, score } = evaluation;

  return (
    <div className='digital-audit-chpanel'>
      <div className='digital-audit-chhead'>
        <div>
          <div className='digital-audit-chname'>{channel.name}</div>
          <div className='digital-audit-chblurb'>{channel.blurb}</div>
        </div>
        <span className={cn('digital-audit-chbadge', score.cls)}>
          {score.label}
          {score.pct !== null && ` · ${score.pct}%`}
        </span>
      </div>
      {results.map(({ label, result: [status, detail] }) => (
        <div key={label} className={cn('digital-audit-chk', status)}>
          <span className='digital-audit-chk-mk'>{CHECK_MARK[status]}</span>
          <span>
            <span className='digital-audit-chk-label'>{label}</span>
            <div className='digital-audit-chk-detail'>{detail}</div>
          </span>
        </div>
      ))}
      <div className='digital-audit-chblurb'>
        ✓ healthy · △ weakness · ✗ missing · • context / not scan-visible
      </div>
      <CommentThread anchor={channelAnchor(channel.id)} />
    </div>
  );
};

export const ChannelHealthView = ({ report }: ChannelHealthViewProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  if (!report) {
    return (
      <div className='digital-audit-page'>
        <div className='digital-audit-empty-state'>Run a scan to see channel health.</div>
      </div>
    );
  }

  const { visible, hiddenNames } = evaluateChannelHealth(
    report.scanResult,
    report.input.clientType
  );
  const active = visible.find(v => v.channel.id === activeId) ?? visible[0];

  return (
    <div className='digital-audit-page'>
      <div className='digital-audit-section'>
        <div className='digital-audit-section-head'>
          <div className='digital-audit-section-title'>Channel health</div>
          <div className='digital-audit-section-count'>
            scored from this scan&apos;s evidence, organized by Tinuiti&apos;s service channels
          </div>
        </div>
        <div className='digital-audit-ch-pills'>
          {visible.map(evaluation => (
            <InteractiveDiv
              key={evaluation.channel.id}
              className={cn('digital-audit-chpill', {
                active: evaluation.channel.id === active?.channel.id
              })}
              onClick={() => setActiveId(evaluation.channel.id)}
            >
              {evaluation.channel.name}
              <span className={cn('digital-audit-chdot', evaluation.score.cls)} />
            </InteractiveDiv>
          ))}
        </div>
        {active && <ChannelPanel evaluation={active} />}
        {hiddenNames.length > 0 && (
          <div className='digital-audit-subtitle'>
            Hidden for {report.journey.clientTypeLabel}: {hiddenNames.join(', ')}
          </div>
        )}
      </div>
    </div>
  );
};
